import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import { useSelector } from 'react-redux';
import { colors } from '../../../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../../../constants/text';
import { formatPlanNameForDisplay } from '../../../../../../Account/Profile/Insurance/util/insuranceDetails';
import DateTime from '../../../../../../DateTime';
import { DATE_SLASH_FORMAT } from '../../../../../../../core/util/date';
import useSelectInsuranceProfile from '../../../../../hooks/useSelectInsuranceProfile';
import { safeGet } from '../../../../../../../core/util/object';

const InfoContainer = styled.div.withConfig({ componentId: 'sc-bs1nbt-0' })`
  display: flex;
  flex-direction: column;
  margin-bottom: 7px;
  gap: 22px;
`;

const FieldLabel = styled.div.withConfig({ componentId: 'sc-bs1nbt-1' })`
  font-size: 10px;
  text-transform: uppercase;
  font-weight: normal;
  text-align: left;
  color: ${colors.lightText};
  white-space: nowrap;
  margin-bottom: 2px;
`;

const FieldContents = styled.div.withConfig({ componentId: 'sc-bs1nbt-2' })`
  color: ${colors.squid};
  font-size: ${fontSize.default};
  font-family: ${fontFamily.book};
  line-height: ${fontSize.extraLarge};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type Props = {
  userProfileId: string;
};

const PlanDetails = (props: Props) => {
  const { userProfileId } = props;
  const detailsResult = useSelector((state: any) => state.insuranceDetails.result);
  const insuranceProfile = useSelectInsuranceProfile(userProfileId);

  const startDate = safeGet(detailsResult)(`${userProfileId}.plan.start_date`);
  const planDescription = safeGet(detailsResult)(`${userProfileId}.plan.description`);
  const groupNumber = safeGet(detailsResult)(`${userProfileId}.plan.group_number`);

  const dateOfBirth = safeGet(insuranceProfile)('birth_date');
  const firstName = safeGet(insuranceProfile)('first_name');
  const lastName = safeGet(insuranceProfile)('last_name');
  const insurerType = safeGet(insuranceProfile)('insurer_type');
  const memberId = safeGet(insuranceProfile)('member_code');

  return (
    <InfoContainer>
      <Grid container direction="column" item xs={12}>
        <Grid item xs={12}>
          {planDescription && <FieldLabel>Plan Name</FieldLabel>}
        </Grid>

        <Grid item xs={12}>
          <FieldContents>{formatPlanNameForDisplay(planDescription)}</FieldContents>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item xs={7}>
          <Grid item xs={9}>
            <FieldLabel>Member Name</FieldLabel>
          </Grid>

          <Grid item xs={10}>
            <FieldContents>{`${firstName} ${lastName}`}</FieldContents>
          </Grid>
        </Grid>

        <Grid container direction="row" item xs={5}>
          <Grid item xs={7}>
            <FieldLabel>{startDate ? 'Effective' : 'Birth Date'}</FieldLabel>
          </Grid>

          <Grid item xs={9}>
            <FieldContents>
              {startDate
                ? DateTime.format(startDate)(DATE_SLASH_FORMAT)
                : DateTime.format(dateOfBirth)(DATE_SLASH_FORMAT)}
            </FieldContents>
          </Grid>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item xs={7}>
          <Grid item xs={6}>
            <FieldLabel>Member ID</FieldLabel>
          </Grid>

          <Grid item xs={10}>
            <FieldContents>{memberId && memberId.toUpperCase()}</FieldContents>
          </Grid>
        </Grid>

        <Grid item xs={5}>
          <Grid item xs={8}>
            <FieldLabel>{groupNumber ? 'Group ID' : 'Plan Type'}</FieldLabel>
          </Grid>

          <Grid item xs={12}>
            <FieldContents>
              {(groupNumber && groupNumber.toUpperCase()) ||
                (insurerType && insurerType.toUpperCase())}
            </FieldContents>
          </Grid>
        </Grid>
      </Grid>
    </InfoContainer>
  );
};

export default PlanDetails;
